import React, { useEffect } from "react";
import { useNotificationStore } from "@/zustand/useNotificationStore";
import { createPortal } from "react-dom";
import Button from "@/components/Button";
import useUnsavedChangesStore from "@/zustand/useUnsavedChangesStore";
import { useNavigate } from "react-router-dom";
import { clearSentryUser } from "@utils/sentry";
import { useLogout } from "@/hooks/useAuth";

interface ToastNotificationProps {
  position?: "bottom-center" | "top-right";
  time?: number;
}

const UnsavedChangesToast: React.FC<ToastNotificationProps> = ({
  time = 5000,
}) => {
  const {
    status,
    showUnsavedChangesNotification,
    setShowUnsavedChangesNotification,
  } = useNotificationStore();

  const { mutate: logout } = useLogout();

  const { pendingNavigation, setHasUnsavedChanges, setPendingNavigation } =
    useUnsavedChangesStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (showUnsavedChangesNotification) {
      const timer = setTimeout(() => {
        setShowUnsavedChangesNotification(false);
      }, time);

      return () => clearTimeout(timer);
    }
  }, [
    status,
    time,
    setShowUnsavedChangesNotification,
    showUnsavedChangesNotification,
  ]);

  if (!showUnsavedChangesNotification) return null;

  const styles = {
    container: `flex items-center gap-4 justify-between w-[425px] pl-[20px] py-1.5 pr-[8px] rounded-[10px] bg-green4 text-green1`,
  };

  const onDiscard = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedChangesNotification(false);
    if (pendingNavigation === "/login") {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        logout(
          { refresh: refreshToken },
          {
            onSuccess: (response) => {
              if (response) {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                clearSentryUser();
                navigate("/login");
              }
            },
            onError: (error) => {
              console.error("Logout Error:", error);
            },
          }
        );
      }
    } else if (pendingNavigation && pendingNavigation !== "/login") {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const toastElement = (
    <div className={"fixed top-8 right-8 z-50"}>
      <div className={styles.container}>
        <p className={"text-green1"}>There are unsaved changes!</p>
        <div className="flex items-center">
          <button
            onClick={onDiscard}
            className="text-green1 hover:text-green2 transition-colors duration-300 text-[15px] font-semibold mr-4">
            Discard
          </button>

          <Button type="submit" form="hook-form" variant="green">
            Save
          </Button>
        </div>
      </div>
    </div>
  );
  return createPortal(toastElement, document.body);
};

export default UnsavedChangesToast;
